import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  Pagination,
  QDataTable,
  QEmptyState,
  QSelectItem,
  QStack,
  Sorting,
  useCurrentUser,
} from '@qualio/ui-components';
import { EventType, TasksRequest, TaskV2, User } from '../../types';
import { tasksTablePersistanceKey } from '../../types/common/constants';
import tasksApi from '../../api/tasks.api';
import { Link } from 'react-router-dom';
import ActiveEventTypeFilter from '../EventTable/components/ActiveEventTypeFilter';
import { useEventTypes, useUsers } from '../../hooks';
import EventTypeFilter from '../EventTable/components/EventTypeFilter';
import ActiveStatusFilter from '../EventTable/components/ActiveStatusFilter';
import StatusFilter from '../EventTable/components/StatusFilter';
import ActiveDateFilter from '../EventTable/components/ActiveDateFilter';
import DateFilter from '../shared/DateFilter/DateFilter';
import UserFilter from '../EventTable/components/UserFilter';
import ActiveUserFilter from '../EventTable/components/ActiveUserFilter';
import DueFilter from '../EventTable/components/DueFilter';
import ActiveDueFilter from '../EventTable/components/ActiveDueFilter';
import { renderDueAtDueColumn } from './util';
import { useFlags } from '../../external/LaunchDarklyAdapter';
import { FilterByCreatedDate } from '../../displayStrings';

const columnHelper = createQColumnHelper<TaskV2>();

export const TaskTableV2: React.FC = () => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const isTasksListSearchBar = useFlags('tasksListSearchBar');
  const { Provider: PaginationProvider } = Pagination.useLimitOffset();
  const { data: fetchedEventTypes, isLoading: isLoadingEventTypes } = useEventTypes();
  const { data: fetchedUsers, isLoading: isLoadingUsers } = useUsers();

  const statusValueLabelMap = useMemo<Record<string, string>>(
    () => ({
      open: t('common.labels.open'),
      closed_success: t('common.labels.completed'),
      closed_fail: t('common.labels.canceled'),
    }),
    [t],
  );

  const eventTypesMap = useMemo(() => {
    return (fetchedEventTypes ?? []).reduce(
      (acc, eventType) => {
        acc[eventType.lineage_id] = eventType;
        return acc;
      },
      {} as Record<string, EventType>,
    );
  }, [fetchedEventTypes]);

  const statusList = useMemo(
    () =>
      Object.keys(statusValueLabelMap).map((key) => ({ value: key, label: statusValueLabelMap[key] }) as QSelectItem),
    [statusValueLabelMap],
  );

  const dueList = useMemo(
    () =>
      [
        { label: t('common.labels.overdue'), value: 'true' },
        { label: t('common.labels.onTime'), value: 'false' },
      ] as QSelectItem[],
    [t],
  );

  const statusMap = useMemo(() => {
    return statusList.reduce(
      (acc, status) => {
        acc[status['value']] = status['label'];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [statusList]);

  const usersMap = useMemo(() => {
    return (fetchedUsers ?? [])?.reduce(
      (acc, fetchedUser) => {
        acc[fetchedUser.id.toString()] = fetchedUser;
        return acc;
      },
      {} as Record<string, User>,
    );
  }, [fetchedUsers]);

  const dueMap = useMemo(() => {
    return dueList.reduce(
      (acc, due) => {
        acc[due['value']] = due['label'];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [dueList]);

  const filterDefinitions: Filtering.FilterDefinitions = useMemo(() => {
    return {
      event_type: {
        label: t('common.labels.eventType'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: (value: unknown) => <ActiveEventTypeFilter value={value} eventTypesMap={eventTypesMap} />,
      },
      status: {
        label: t('common.labels.status'),
        schema: Filtering.schemas.StringSchema().array().nullable(),
        activeRender: (value: unknown) => <ActiveStatusFilter value={value} statusMap={statusMap} />,
        defaultValue: ['open'],
      },
      owner_id: {
        label: t('common.labels.assignee'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: (value: unknown) => <ActiveUserFilter value={value} usersMap={usersMap} />,
      },
      is_overdue: {
        label: t('common.labels.due'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: (value: unknown) => <ActiveDueFilter value={value} dueMap={dueMap} />,
      },
      created_at_start_date: {
        label: t('common.labels.createdAfter'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: (value: unknown) => <ActiveDateFilter value={value} />,
      },
      created_at_end_date: {
        label: t('common.labels.createdBefore'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: (value: unknown) => <ActiveDateFilter value={value} />,
      },
    };
  }, [eventTypesMap, statusMap, usersMap, dueMap, t]);

  const columns = useMemo(() => {
    return [
      columnHelper.id((task) => task.attributes.code, {
        header: t('common.labels.id'),
        id: 'code',
        minWidth: '100px',
        maxWidth: '150px',
      }),
      {
        accessorKey: 'title',
        header: t('common.labels.title'),
        cell: ({ row: { original: task } }: { row: { original: TaskV2 } }) => {
          return <Link to={`events/${task.attributes.issue_id}?task_id=${task.id}`}>{task.attributes.title}</Link>;
        },
        meta: {
          minWidth: '200px',
        },
      },
      columnHelper.text((task) => task.attributes.issue_code, {
        header: t('common.labels.eventId'),
        id: 'issue_code',
        minWidth: '100px',
        maxWidth: '150px',
      }),
      columnHelper.text((task) => task.attributes.owner_full_name, {
        header: t('common.labels.assignee'),
        id: 'owner',
        minWidth: '100px',
        maxWidth: '250px',
      }),
      columnHelper.text((task) => renderDueAtDueColumn(task), {
        header: t('common.labels.due'),
        id: 'due_at',
        minWidth: '100px',
        maxWidth: '150px',
      }),
      columnHelper.status((task) => statusValueLabelMap[task.attributes.status] as string, {
        header: t('common.labels.status'),
        id: 'status',
        statuses: {
          [t('common.labels.open')]: 'blue',
          [t('common.labels.completed')]: 'green',
          [t('common.labels.canceled')]: 'red',
        },
        minWidth: '100px',
        maxWidth: '150px',
      }),
    ].filter(Boolean);
  }, [t, statusValueLabelMap]);

  const fetchTasks = async ({
    pageParam,
  }: {
    pageParam: DataProvider.PageParams;
  }): Promise<DataProvider.TDataWithCount<TaskV2>> => {
    const pagination = pageParam.pagination;
    const sorting = pageParam.sorting;
    const searchTerm = pageParam.searchTerm;
    const filterObjects = pageParam.filters ?? {};
    const filters = Object.fromEntries(Object.entries(filterObjects).map(([k, v]) => [k, v.value]));

    const requestParams: TasksRequest = {
      ...filters,
      page_number: pagination ? pagination.pageIndex + 1 : 1,
      page_size: pagination ? pagination.pageSize : 15,
    };

    if (searchTerm) {
      requestParams['term'] = searchTerm;
    } else if (sorting?.column) {
      const prefix = sorting.descending ? '-' : '';
      requestParams['order_by'] = `${prefix}${sorting.column}`;
    }
    const results = await tasksApi.getAllTasks(companyId, requestParams);

    return {
      itemCount: results?.meta.totalItems,
      data: results?.data ?? [],
    };
  };

  return (
    <>
      <PaginationProvider>
        <Filtering.FilterProvider
          searchTermKey={isTasksListSearchBar ? 'term' : null}
          definitions={filterDefinitions}
          persistanceKey={tasksTablePersistanceKey}
        >
          <Sorting.DefaultSortingProvider sortByKey="order_by" persistenceKey={tasksTablePersistanceKey}>
            <DataProvider.Remote
              queryFn={fetchTasks}
              queryKey={['getTasksV2', companyId]}
              queryParamKeys={{
                searchTerm: 'term',
                sortBy: 'order_by',
                pageSize: 'limit',
                pageIndex: 'offset',
              }}
            >
              <QDataTable columns={columns} enableSorting data-cy="tasks-table" getRowId={(row) => row.id.toString()}>
                <Filtering.FormContent>
                  <QStack spacing="16px" padding="4px">
                    <QStack direction="row" spacing="16px">
                      <EventTypeFilter isLoadingEventTypes={isLoadingEventTypes} eventTypes={fetchedEventTypes ?? []} />
                      <StatusFilter statusList={statusList} />
                    </QStack>
                    <QStack direction="row" spacing="16px">
                      <UserFilter
                        label={t('common.labels.assignee')}
                        isLoadingUsers={isLoadingUsers}
                        users={fetchedUsers ?? []}
                        placeholderText={t('common.filters.filterByAssignee')}
                      />
                      <DueFilter dueList={dueList} />
                    </QStack>
                    <QStack direction="row" spacing="16px">
                      <DateFilter
                        name="created_at_start_date"
                        label={t('common.labels.createdAfter')}
                        placeholder={FilterByCreatedDate}
                        dataCy="created-after-filter"
                      />
                      <DateFilter
                        name="created_at_end_date"
                        label={t('common.labels.createdBefore')}
                        placeholder={FilterByCreatedDate}
                        dataCy="created-before-filter"
                      />
                    </QStack>
                  </QStack>
                </Filtering.FormContent>
                <QEmptyState title={t('common.tables.noMatchingTasksFound')} data-cy="tasks-table-empty-state" />
              </QDataTable>
            </DataProvider.Remote>
          </Sorting.DefaultSortingProvider>
        </Filtering.FilterProvider>
      </PaginationProvider>
    </>
  );
};
